import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { ButtonLink } from "../ui";
import { useSession } from "../../lib/session";
import { cn } from "../../lib/cn";

const NAV = [
  { to: "/discover", label: "Explore data" },
  { to: "/developers", label: "Developers" },
  { to: "/partners/founding-data", label: "Data owners" },
  { to: "/data-wanted", label: "Data wanted" },
  { to: "/docs", label: "Docs" },
  { to: "/security", label: "Trust" },
];

export function PublicLayout() {
  const { me } = useSession();
  const [open, setOpen] = useState(false);
  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 border-b border-border bg-bg/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-8 px-4 sm:px-6">
          <Logo />
          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={({ isActive }) => cn("rounded-md px-3 py-1.5 text-sm transition-colors", isActive && !n.to.includes("#") ? "text-fg" : "text-fg-2 hover:text-fg")}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto hidden items-center gap-2 md:flex">
            {me ? (
              <ButtonLink to="/ask" variant="primary" size="sm">
                Search your data
              </ButtonLink>
            ) : (
              <>
                <ButtonLink to="/login" variant="ghost" size="sm">
                  Sign in
                </ButtonLink>
                <ButtonLink to="/partners/founding-data" variant="ghost" size="sm">
                  Publish data
                </ButtonLink>
                <ButtonLink to="/developers" variant="primary" size="sm">
                  Get API key
                </ButtonLink>
              </>
            )}
          </div>
          <button className="ml-auto rounded p-2 text-fg-2 md:hidden" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
        {open && (
          <nav className="border-t border-border px-4 py-3 md:hidden" aria-label="Mobile">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block rounded px-2 py-2 text-sm text-fg-2 hover:text-fg">
                {n.label}
              </Link>
            ))}
            <div className="mt-2 flex gap-2">
              <ButtonLink to="/login" size="sm">
                Sign in
              </ButtonLink>
              <ButtonLink to="/developers" variant="primary" size="sm">
                Get API key
              </ButtonLink>
            </div>
          </nav>
        )}
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function Footer() {
  const cols = [
    { h: "Network", items: [["Explore live data", "/discover"], ["Data wanted by AI", "/data-wanted"], ["Network stats", "/network"], ["Marketplace", "/marketplace"], ["Pricing", "/pricing"]] },
    { h: "Developers", items: [["One API for live data", "/developers"], ["MCP integration", "/integrations/mcp"], ["API reference", "/docs"], ["Connector", "/docs#connector"]] },
    { h: "Data owners", items: [["Founding Data Partners", "/partners/founding-data"], ["What AI is requesting", "/data-wanted"], ["Publish data", "/partners/founding-data#apply"]] },
    { h: "Trust", items: [["Security", "/security"], ["Data exposure contracts", "/security#contracts"], ["Privacy by design", "/security#privacy"]] },
  ];
  return (
    <footer className="border-t border-border bg-bg-elev">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 md:grid-cols-[1.4fr_repeat(4,1fr)]">
        <div>
          <Logo />
          <p className="mt-3 max-w-xs text-sm text-fg-2">The data network for AI agents. Live proprietary data through one API; data owners control what they share and earn when AI uses it.</p>
        </div>
        {cols.map((c) => (
          <div key={c.h}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-fg-3">{c.h}</h3>
            <ul className="mt-3 space-y-2">
              {c.items.map(([l, to]) => (
                <li key={l}>
                  <Link to={to!} className="text-sm text-fg-2 hover:text-fg">
                    {l}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-border py-4 text-center text-xs text-fg-3">© {new Date().getFullYear()} DBIndex.ai</div>
    </footer>
  );
}
